/**
 * Legal content registry (ADR-088) — the single point where a `brandSlug` is
 * resolved to brand-scoped legal content, versions, and URLs. Consumers
 * should go through `getLegalContent(brandSlug)` instead of importing the
 * `ForBrand`-suffixed primitives directly — adding a 3rd brand means one
 * entry in `brand.ts`/`version.ts`/`version-history.ts` plus a content
 * directory, zero changes to any consumer's call signature (OCP; H2
 * anti-pattern avoided — `brandSlug` is resolved ONCE here, not threaded
 * through 7 separate function signatures).
 *
 * BROWSER-SAFE — imports only the generated, pre-sanitized HTML constant
 * (`src/generated/sanitized-legal-html.ts`), never `sanitized-html.ts`
 * (which reads from disk). Safe to import from `src/index.ts`.
 *
 * Fail-closed vs fallback (ADR-088 "Failure mode"):
 * - CONTENT path (`getSanitizedHtml`, `getCurrentVersionEntry`) — an
 *   unknown/missing `brandSlug` THROWS (C1). Silently rendering another
 *   brand's legal text would be a real leak (e.g. a HotelB2B user seeing
 *   KiedyNabory's "Baza Danych o Naborach" anti-scraping clauses).
 * - VERSION-COMPARISON / metadata path (`currentVersion`, `isVersionCurrent`,
 *   `urls`, `history`) — falls back to `kiedynabory`. Version labels carry a
 *   brand suffix (`-kn`/`-hb`), so a lost `brandSlug` here produces a loud,
 *   detectable `LEGAL_VERSION_MISMATCH` rather than a silent content leak.
 */
import { BrandSlug, FALLBACK_BRAND_SLUG, isKnownBrandSlug } from './brand';
import {
  LegalDocumentType,
  LegalVersionHistoryEntry,
  currentVersionForBrand,
  getCurrentVersionEntryForBrand,
  historyForBrand,
  isVersionCurrentForBrand,
} from './version-history';
import { LEGAL_DOCUMENT_URLS_BY_BRAND } from './legal-document-urls';
import { SANITIZED_LEGAL_HTML } from './generated/sanitized-legal-html';

/** Brand-scoped legal content/version accessor returned by {@link getLegalContent}. */
export interface BrandLegalContent {
  /**
   * The brand this instance resolved to on the FALLBACK path — always a
   * known brand (never throws to produce this value). Equals the requested
   * `brandSlug` when it was known, otherwise `kiedynabory`.
   */
  readonly brandSlug: BrandSlug;
  currentVersion(documentType: LegalDocumentType): string;
  isVersionCurrent(documentType: LegalDocumentType, version: string): boolean;
  /** CONTENT path — throws for an unknown/missing `brandSlug` (fail-closed, C1). */
  getSanitizedHtml(documentType: LegalDocumentType): string;
  /** CONTENT path — throws for an unknown/missing `brandSlug` (fail-closed, C1). */
  getCurrentVersionEntry(documentType: LegalDocumentType): LegalVersionHistoryEntry;
  readonly urls: Record<LegalDocumentType, string>;
  readonly history: readonly LegalVersionHistoryEntry[];
}

function requireKnownBrand(brandSlug: unknown): BrandSlug {
  if (!isKnownBrandSlug(brandSlug)) {
    throw new Error(
      `[legal-content] Unknown or missing brandSlug on the legal CONTENT path: ${JSON.stringify(
        brandSlug,
      )}. Refusing to fall back to "${FALLBACK_BRAND_SLUG}" — see ADR-088 (fail-closed, C1).`,
    );
  }
  return brandSlug;
}

/**
 * Single point of brand resolution for legal content (ADR-088). `brandSlug`
 * may be unknown, `undefined`, or `null` — the returned object still works
 * for the version-comparison / metadata methods (fallback to
 * `kiedynabory`); only the two CONTENT methods throw when actually invoked.
 */
export function getLegalContent(
  brandSlug?: string | null,
): BrandLegalContent {
  const known = isKnownBrandSlug(brandSlug);
  const fallbackBrand: BrandSlug = known ? brandSlug : FALLBACK_BRAND_SLUG;

  return {
    brandSlug: fallbackBrand,
    currentVersion: (documentType) =>
      currentVersionForBrand(fallbackBrand, documentType),
    isVersionCurrent: (documentType, version) =>
      isVersionCurrentForBrand(fallbackBrand, documentType, version),
    getCurrentVersionEntry: (documentType) =>
      getCurrentVersionEntryForBrand(requireKnownBrand(brandSlug), documentType),
    getSanitizedHtml: (documentType) => {
      const brand = requireKnownBrand(brandSlug);
      return SANITIZED_LEGAL_HTML[brand][documentType];
    },
    urls: LEGAL_DOCUMENT_URLS_BY_BRAND[fallbackBrand],
    history: historyForBrand(fallbackBrand),
  };
}
